'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertTriangle, FeatherCalendar, FeatherChevronRight, FeatherEdit2, FeatherMoreHorizontal, FeatherPackage, FeatherTrendingUp, FeatherXCircle } from '@subframe/core';
import * as SubframeCore from '@subframe/core';
import { useEffect, useRef } from 'react';
import { DropdownMenu } from '@/shared/components/ui';
import type { TrialTenant } from '../types';
import { calcularDiasRestantes, formatarDataCurta, formatarDiasRestantes, isExpiradoHaMaisDe, isProximoVencimento } from '../utils/trialDateUtils';
import { TrialStatusBadge } from './TrialStatusBadge';
interface TrialListTableProps {
  trials: TrialTenant[];
  canManage: boolean;
  highlightTenantId?: string | null;
  highlightProductId?: string | null;
  onEstender: (trial: TrialTenant) => void;
  onConverter: (trial: TrialTenant) => void;
  onEncerrar: (trial: TrialTenant) => void;
  onAbrirTenant: (trial: TrialTenant) => void;
}
const EXPIRED_HIGHLIGHT_AFTER_DAYS = 30;
const TERMINAL_EVENT_BY_STATUS: Record<string, ReadonlyArray<string>> = {
  CONVERTIDO: ['CONVERSAO'],
  ENCERRADO: ['ENCERRAMENTO_MANUAL', 'BLOQUEIO_AUTOMATICO']
};
function resolveTerminalDate(trial: TrialTenant): string | null {
  const acoes = TERMINAL_EVENT_BY_STATUS[trial.status];
  if (!acoes) return null;
  const evento = [...trial.historico].sort((a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime()).find(e => acoes.includes(e.acao));
  return evento?.dataHora ?? trial.atualizadoEm;
}
export function TrialListTable(t0) {
  const $ = _c(18);
  const {
    trials,
    canManage,
    highlightTenantId,
    highlightProductId,
    onEstender,
    onConverter,
    onEncerrar,
    onAbrirTenant
  } = t0;
  const highlightedRef = useRef(null);
  let t1;
  if ($[0] !== highlightTenantId) {
    t1 = () => {
      if (!highlightTenantId || !highlightedRef.current) {
        return;
      }
      highlightedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    $[0] = highlightTenantId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== highlightProductId || $[3] !== highlightTenantId || $[4] !== trials) {
    t2 = [highlightTenantId, highlightProductId, trials];
    $[2] = highlightProductId;
    $[3] = highlightTenantId;
    $[4] = trials;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="hidden grid-cols-[2.2fr_1fr_1fr_1.1fr_1.1fr_1fr_72px] gap-4 border-b border-solid border-neutral-100 bg-neutral-50/60 px-5 py-3 md:grid"><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Cliente</span><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Início</span><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Expira em</span><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Tempo restante</span><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Encerramento</span><span className="text-caption-bold font-caption-bold text-subtext-color uppercase">Status</span><span className="sr-only">Ações</span></div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== canManage || $[8] !== highlightProductId || $[9] !== highlightTenantId || $[10] !== onAbrirTenant || $[11] !== onConverter || $[12] !== onEncerrar || $[13] !== onEstender || $[14] !== trials) {
    t4 = trials.map(trial => {
      const isTerminal = trial.status === "CONVERTIDO" || trial.status === "ENCERRADO";
      const terminalDate = isTerminal ? resolveTerminalDate(trial) : null;
      const diasRestantes = calcularDiasRestantes(trial.dataExpiracao);
      const venceLogo = trial.status === "ATIVO" && isProximoVencimento(trial.dataExpiracao, trial.alertaVencimentoDias);
      const expiradoHaMuito = trial.status === "EXPIRADO" && isExpiradoHaMaisDe(trial.dataExpiracao, EXPIRED_HIGHLIGHT_AFTER_DAYS);
      const isHighlighted = !!highlightTenantId && trial.tenantId === highlightTenantId && (!highlightProductId || trial.produtoId === highlightProductId);
      return <li key={trial.id} ref={isHighlighted ? highlightedRef : undefined} className={`grid grid-cols-1 gap-2 px-5 py-4 transition-colors md:grid-cols-[2.2fr_1fr_1fr_1.1fr_1.1fr_1fr_72px] md:items-center md:gap-4 ${isHighlighted ? "bg-brand-50/40 ring-brand-300 relative z-10 rounded-[8px] ring-2 ring-inset" : expiradoHaMuito ? "bg-error-50/30" : venceLogo ? "bg-warning-50/40" : "hover:bg-neutral-50"}`}><div className="flex min-w-0 flex-col gap-1"><button type="button" onClick={() => onAbrirTenant(trial)} className="text-body-bold font-body-bold text-default-font hover:text-brand-700 truncate text-left">{trial.tenantNome}</button>{trial.tenantEmail && <span className="text-caption font-caption text-subtext-color truncate">{trial.tenantEmail}</span>}<span className="text-caption font-caption text-brand-700 mt-0.5 flex items-center gap-1"><FeatherPackage className="h-3 w-3 shrink-0" /><span className="truncate">{trial.produtoNome}</span></span>{venceLogo && <span className="text-caption-bold font-caption-bold text-warning-700 mt-1 flex items-center gap-1"><FeatherAlertTriangle className="h-3 w-3" />Vence em breve</span>}{expiradoHaMuito && <span className="text-caption-bold font-caption-bold text-error-700 mt-1 flex items-center gap-1"><FeatherAlertTriangle className="h-3 w-3" />Expirado sem ação há mais de {EXPIRED_HIGHLIGHT_AFTER_DAYS} dias</span>}</div><div className="flex items-center gap-2 text-sm text-neutral-700"><FeatherCalendar className="h-3.5 w-3.5 text-neutral-400 md:hidden" /><span>{formatarDataCurta(trial.dataInicio)}</span></div><div className="flex items-center gap-2 text-sm text-neutral-700"><FeatherCalendar className="h-3.5 w-3.5 text-neutral-400 md:hidden" /><span>{formatarDataCurta(trial.dataExpiracao)}</span></div><div className="text-sm text-neutral-700">{isTerminal ? <span className="text-neutral-500">—</span> : <span className={trial.status === "ATIVO" && diasRestantes <= 7 ? "text-warning-700 font-medium" : trial.status === "EXPIRADO" ? "text-error-700 font-medium" : "text-neutral-700"}>{formatarDiasRestantes(diasRestantes)}</span>}</div><div className="flex items-center gap-2 text-sm text-neutral-700"><FeatherCalendar className="h-3.5 w-3.5 text-neutral-400 md:hidden" /><span>{terminalDate ? formatarDataCurta(terminalDate) : <span className="text-neutral-500">—</span>}</span></div><div><TrialStatusBadge status={trial.status} /></div><div className="flex justify-end">{(trial.status === "ATIVO" || trial.status === "EXPIRADO") && canManage ? <SubframeCore.DropdownMenu.Root><SubframeCore.DropdownMenu.Trigger asChild={true}><button type="button" aria-label={"Mais a\xE7\xF5es"} className="bg-default-background flex h-8 w-8 items-center justify-center rounded-[8px] border border-solid border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-50"><FeatherMoreHorizontal className="h-4 w-4" /></button></SubframeCore.DropdownMenu.Trigger><SubframeCore.DropdownMenu.Portal><SubframeCore.DropdownMenu.Content side="bottom" align="end" sideOffset={4}><DropdownMenu><DropdownMenu.DropdownItem icon={<FeatherEdit2 />} onClick={() => onEstender(trial)}>Estender</DropdownMenu.DropdownItem><DropdownMenu.DropdownItem icon={<FeatherTrendingUp />} onClick={() => onConverter(trial)}>Converter para plano pago</DropdownMenu.DropdownItem><DropdownMenu.DropdownDivider /><DropdownMenu.DropdownItem icon={<FeatherXCircle />} className="text-error-600" onClick={() => onEncerrar(trial)}>Encerrar trial</DropdownMenu.DropdownItem></DropdownMenu></SubframeCore.DropdownMenu.Content></SubframeCore.DropdownMenu.Portal></SubframeCore.DropdownMenu.Root> : <button type="button" aria-label="Abrir ficha" onClick={() => onAbrirTenant(trial)} className="bg-default-background flex h-8 w-8 items-center justify-center rounded-[8px] border border-solid border-neutral-200 text-neutral-500 transition-colors hover:bg-neutral-50"><FeatherChevronRight className="h-4 w-4" /></button>}</div></li>;
    });
    $[7] = canManage;
    $[8] = highlightProductId;
    $[9] = highlightTenantId;
    $[10] = onAbrirTenant;
    $[11] = onConverter;
    $[12] = onEncerrar;
    $[13] = onEstender;
    $[14] = trials;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <div className="bg-default-background w-full overflow-hidden rounded-[16px] border border-solid border-neutral-200">{t3}<ul className="divide-y divide-neutral-100">{t4}</ul></div>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}